<template>
  <PopupContent class="notification-content">
    <PopupTitle class="flex align-center">Сповіщення <span class="messages-counter">{{ unreadNotificationsCount }}</span></PopupTitle>
    <div ref="notificationsContainer" class="notifications-wrapper">
      <NotificationsListSkeleton :is-loading="isLoading">
        <NotificationsList
          v-if="newNotifications.length"
          title="Нові"
          :notifications="newNotifications"
          @open-notification="openMessage"
        >
          <template v-slot:title>
            <ToggleSwitch class="flex align-center ml-3" v-model="isChecked" @input="handleToggle">Тільки непрочитані</ToggleSwitch>
          </template>
        </NotificationsList>

        <NotificationsList
          v-if="lastWeekNotifications.length"
          title="Останні 7 днів"
          :notifications="lastWeekNotifications"
          @open-notification="openMessage"
        >
          <template v-slot:title v-if="!newNotifications.length">
            <ToggleSwitch class="flex align-center ml-3" v-model="isChecked" @input="handleToggle">Тільки непрочитані</ToggleSwitch>
          </template>
        </NotificationsList>

        <NotificationsList
          v-if="lastMonthNotifications.length"
          title="Останні 30 днів"
          :notifications="lastMonthNotifications"
          @open-notification="openMessage"
        >
          <template v-slot:title v-if="!newNotifications.length && !lastWeekNotifications.length">
            <ToggleSwitch class="flex align-center ml-3" v-model="isChecked" @input="handleToggle">Тільки непрочитані</ToggleSwitch>
          </template>
        </NotificationsList>

        <NotificationsList
          v-if="olderNotifications.length"
          title="Старіші"
          :notifications="olderNotifications"
          @open-notification="openMessage"
        >
          <template v-slot:title v-if="!newNotifications.length && !lastWeekNotifications.length && !lastMonthNotifications.length">
            <ToggleSwitch class="flex align-center ml-3" v-model="isChecked" @input="handleToggle">Тільки непрочитані</ToggleSwitch>
          </template>
        </NotificationsList>

        <div class="data-loader" v-if="infiniteScroll.loading">
          <SpinnerLoader />
        </div>

        <div class="unread-toggler" v-if="!isEmptyNotificationsList && (getShowOnlyUnreadMessages && unreadNotificationsCount === 0)">
          <ToggleSwitch
            class="flex justify-content-end align-center ml-3"
            v-model="isChecked"
            @input="handleToggle"
          >
            Тільки непрочитані
          </ToggleSwitch>
          <Divider type="horizontal" class="unread-toggler-divider" />
        </div>

        <div class="empty-list" v-if="isEmptyNotificationsList || (getShowOnlyUnreadMessages && unreadNotificationsCount === 0)">
          <p class="message-title" v-html="emptyListMessage">️</p>
        </div>
      </NotificationsListSkeleton>
      <NotificationMessagePopup ref="notificationMessagePopup" />
    </div>
  </PopupContent>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { LAST_UNREAD_MESSAGE_POPUP_EVENTS } from '../../config';
import { notificationsFilter } from '../../lib';
import NotificationMessagePopup from '../notification-message-popup';
import NotificationsList from './notifications-list';
import NotificationsListSkeleton from './notifications-list-skeleton';
import { notificationListEmptyMessage } from '@/features/server-notifications/config';
import { toggleSwitch } from '@/shared/lib/form';
import { infiniteScroll } from '@/shared/lib/scroll';
import { Divider } from '@/shared/ui/divider';
import { ToggleSwitch } from '@/shared/ui/form';
import { SpinnerLoader } from '@/shared/ui/loader';
import { PopupContent, PopupTitle } from '@/shared/ui/popup';

export default {
  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    unreadNotificationsCount: {
      type: Number,
      default: 0
    },
    notifications: {
      type: Array,
      default: () => [],
    },
    loadMoreCallback: {
      type: Function,
      required: true,
    },
    enableInfiniteScroll: {
      type: Boolean,
      default: true,
    }
  },
  mounted() {
    this.isChecked = this.getShowOnlyUnreadMessages;
    this.infiniteScroll.disable = this.isLoading || this.getShowOnlyUnreadMessages || !this.enableInfiniteScroll;
    this.infiniteScroll.container = this.$refs.notificationsContainer;
    this.infiniteScroll.distance = 30;
  },
  mixins: [ toggleSwitch, notificationsFilter, infiniteScroll ],
  components: {
    NotificationsListSkeleton, PopupContent, PopupTitle, ToggleSwitch, NotificationsList, SpinnerLoader, Divider,
    NotificationMessagePopup
  },
  computed: {
    ...mapGetters('serverNotificationsModule', ['getShowOnlyUnreadMessages']),
    isEmptyNotificationsList() {
      return this.notifications?.length === 0 || this.notifications === 'undefined';
    },
    emptyListMessage() {
      return notificationListEmptyMessage.description;
    },
  },
  methods: {
    ...mapActions('serverNotificationsModule', ['setShowOnlyUnreadMessages', 'getNotificationMessage']),
    async loadMore() {
      return await this.loadMoreCallback({ page: this.infiniteScroll.page });
    },
    async openMessage(notification) {
      if (notification?.read === false) {
        const serverNotification = await this.getNotificationMessage(notification?.id || 0);
        this.$refs.notificationMessagePopup.open(serverNotification);
        this.$eventBus.$emit(LAST_UNREAD_MESSAGE_POPUP_EVENTS.LAST_UNREAD_MESSAGE_POPUP_CLOSE);
      } else {
        this.$refs.notificationMessagePopup.open(notification);
      }
    },
  },
  watch: {
    isChecked(newValue, oldValue) {
      if (oldValue !== newValue && newValue !== this.getShowOnlyUnreadMessages) {
        this.setShowOnlyUnreadMessages(newValue);
        this.infiniteScroll.disable = !newValue || this.isLoading && !this.enableInfiniteScroll;
      }
    },
    enableInfiniteScroll(newValue, oldValue) {
      if (oldValue !== newValue) {
        this.infiniteScroll.disable = !newValue || this.isLoading || !this.isChecked;
      }
    },
    isLoading(newValue, oldValue) {
      if (oldValue !== newValue) {
        this.infiniteScroll.disable = newValue || this.isChecked || !this.enableInfiniteScroll;
      }
    },
  }
};
</script>

<style lang="scss" scoped>
.notification-content {
  width: 400px;

  @media (max-width: 550px) {
    width: 100%;
  }
}

.notifications-wrapper {
  max-height: 400px;
  overflow: scroll;
  padding-right: 18px;
}

.messages-counter {
  background-color: #FB923C;
  border-radius: 50%;
  min-width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  margin-left: 8px;
  color: #ffffff;
  font-size: 12px;
  font-weight: bold;
}

.notification-subtitle {
 font-size: 14px;
}

.message-title {
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 20px;
  text-align: center;
}

.unread-toggler {
  &-divider {
    margin-top: 5px;
  }
}
</style>
